/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { isEmpty, StepProps, Text, TextColor, Theme, useTheme, VFlow } from 'bold-ui'
import { VerticalStep, VerticalStepper } from 'components/vertical-stepper'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { useMemo } from 'react'

import {
  ConsultaSeguimentoAplicacaoItemModel,
  ConsultaSeguimentoAplicacaoModel,
  ConsultaSeguimentoModalTab,
  ConsultaSeguimentoPerguntaStatusEnum,
} from '../model-consultaSeguimento'
import { getPerguntaAtiva } from '../utils-consultaSeguimento'
import { ConsultaSeguimentoModalStepperPerguntaStatus } from './ConsultaSeguimentoModalStepperPerguntaStatus'

interface ConsultaSeguimentoModalStepperProps {
  aplicacao: ConsultaSeguimentoAplicacaoModel
  status: ConsultaSeguimentoModalTab
}

export function ConsultaSeguimentoModalStepper(props: Readonly<ConsultaSeguimentoModalStepperProps>) {
  const { status, aplicacao } = props

  const theme = useTheme()

  const hasValues = !isEmpty(aplicacao)
  const perguntas = hasValues ? Object.values(aplicacao) : []
  const numberOfPerguntas = perguntas.length

  const { indexOfPerguntaAtiva } = getPerguntaAtiva(aplicacao)
  const entrevistaSeguimentoStep = getEntrevistaSeguimentoStepDataByStatus(status)

  const numberOfPerguntasNaoDisponiveis = perguntas.filter(
    ({ status }) => status === ConsultaSeguimentoPerguntaStatusEnum.NAO_DISPONIVEL
  ).length

  const isAplicacao = status === ConsultaSeguimentoModalTab.APLICACAO
  const isSelecao = status === ConsultaSeguimentoModalTab.SELECAO
  const isResultado = status === ConsultaSeguimentoModalTab.RESULTADO

  const styles = useMemo(
    () => createStyles(theme, numberOfPerguntas, numberOfPerguntasNaoDisponiveis, indexOfPerguntaAtiva),
    [indexOfPerguntaAtiva, numberOfPerguntas, numberOfPerguntasNaoDisponiveis, theme]
  )

  return (
    <VerticalStepper gap={isResultado ? 2 : 0}>
      <VerticalStep
        status={isSelecao ? 'active' : 'completed'}
        connectorStatus={isSelecao ? 'incompleted' : 'completed'}
        headerStyle={styles.stepHeader}
        connectorStyle={styles.stepConnector}
        header={<Text color={isSelecao ? 'primary' : 'normal'}>Seleção dos itens</Text>}
        body={
          isSelecao && <Text color='secondary'>Selecione os itens do M-CHAT-R que a criança falhou para iniciar.</Text>
        }
      />
      <VerticalStep
        status={entrevistaSeguimentoStep.status}
        connectorStatus={isResultado ? 'completed' : 'incompleted'}
        headerStyle={styles.stepHeader}
        connectorStyle={styles.stepConnector}
        header={
          <Text color={entrevistaSeguimentoStep.textColor}>
            Entrevista de seguimento
            {isAplicacao && (
              <span css={[styles.stepConnector, styles.growingConnector]}>
                {Array.from({ length: numberOfPerguntas + 2 }).map((_, index) => (
                  <span key={`segment-${index + 1}`} css={styles.growingConnectorSegment(index + 1)} />
                ))}
              </span>
            )}
          </Text>
        }
        body={
          isAplicacao && (
            <VFlow vSpacing={0}>
              {Object.entries(aplicacao).map(
                ([pergunta, values]: [MChatPerguntaEnum, ConsultaSeguimentoAplicacaoItemModel]) => (
                  <ConsultaSeguimentoModalStepperPerguntaStatus
                    key={pergunta}
                    pergunta={pergunta}
                    status={values.status}
                  />
                )
              )}
            </VFlow>
          )
        }
      />
      <VerticalStep
        status={isResultado ? 'active' : 'incompleted'}
        header={<Text color={isResultado ? 'primary' : 'secondary'}>Resultado final</Text>}
        headerStyle={styles.stepHeader}
        hasConnector={false}
      />
    </VerticalStepper>
  )
}

function getEntrevistaSeguimentoStepDataByStatus(
  status: ConsultaSeguimentoModalTab
): { status: StepProps['status']; textColor: TextColor } {
  if (status === ConsultaSeguimentoModalTab.SELECAO) {
    return {
      status: 'incompleted',
      textColor: 'secondary',
    }
  } else if (status === ConsultaSeguimentoModalTab.APLICACAO) {
    return {
      status: 'active',
      textColor: 'primary',
    }
  } else {
    return {
      status: 'completed',
      textColor: 'normal',
    }
  }
}

const createStyles = (
  theme: Theme,
  numberOfPerguntas: number,
  numberOfPerguntasNaoDisponiveis: number,
  indexOfPerguntaAtiva: number
) => {
  const numberOfPerguntasDisponiveis = numberOfPerguntas - numberOfPerguntasNaoDisponiveis

  const gridSegmentSizes = [
    '0.5rem',
    ...Array(numberOfPerguntasDisponiveis).fill('1fr'),
    ...Array(numberOfPerguntasNaoDisponiveis).fill('2rem'),
    '0.5rem',
  ].join(' ')

  const isFirstQuestion = indexOfPerguntaAtiva === 0
  const isLastQuestion = indexOfPerguntaAtiva === numberOfPerguntas
  const currentSegmentIndex = (isLastQuestion ? numberOfPerguntas + 1 : indexOfPerguntaAtiva) + 2 // + 2 para os segmentos do inicio e fim

  return {
    stepHeader: css`
      min-height: 2rem;
      height: 2rem;
    `,
    stepConnector: css`
      top: 1.5rem;
      height: calc(100% - 1rem);
    `,
    growingConnector: css`
      width: 0.125rem;
      display: grid;
      grid-template-rows: ${gridSegmentSizes};
      position: absolute;
      left: 0.688rem;
      border-radius: 4px;
      overflow: hidden;
    `,
    growingConnectorSegment: (index: number) => css`
      background: ${theme.pallete.primary.main};
      transform-origin: top;
      transform: scaleY(${index < currentSegmentIndex && !isFirstQuestion ? 1 : 0});
      opacity: ${index < currentSegmentIndex && !isFirstQuestion ? 1 : 0};
      transition: transform 0.3s ease, opacity 0.3s ease;
    `,
  }
}
